"use client";

/**
 * <TabBar /> — chrome 스타일 탭 바 (사용자 스크린샷 기반 정확 재현 2026-05-09).
 *
 * Chrome 패턴:
 *   - bar 배경 = 살짝 어두운 (gray-100), 탭 위쪽 4px gap
 *   - active 탭: 페이지와 동일 배경 (paper), 위 둥근 모서리, 페이지로 자연스럽게 merge
 *   - inactive 탭: bar 와 동일 배경, 좌측 1px separator line (탭 사이 구분)
 *   - X 닫기 버튼: 항상 보임 (hover 시 진해짐)
 *   - 탭 높이: 36px (h-9)
 */

import { Plus, X, Home, LayoutDashboard, LineChart } from "lucide-react";
import { useTabs, type TabType } from "@/contexts/tabs-context";

function TabIcon({ type }: { type: TabType }) {
  if (type === "home") return <Home className="h-3.5 w-3.5 shrink-0" />;
  if (type === "dashboard") return <LayoutDashboard className="h-3.5 w-3.5 shrink-0" />;
  return <LineChart className="h-3.5 w-3.5 shrink-0" />;
}

export function TabBar() {
  const { tabs, activeTabId, openTab, closeTab, activateTab } = useTabs();

  if (tabs.length === 0) return null;

  return (
    <div
      role="tablist"
      aria-label="열린 탭"
      // bar 배경 = paper (사이드바와 동일색 — 사용자 결정 2026-05-09)
      // pt-2 (8px) — chrome 정확 위 mini gap (이전 pt-1 너무 좁음)
      className="sticky top-0 z-30 flex items-end overflow-x-auto bg-[var(--bs-paper)] pt-[var(--bs-space-2)] pl-[var(--bs-space-3)] pr-[var(--bs-space-2)]"
    >
      {tabs.map((tab, idx) => {
        const active = tab.id === activeTabId;
        const prevActive = idx > 0 && tabs[idx - 1].id === activeTabId;
        // separator: 직전 탭이 active 아니고 본 탭도 active 아니면 좌측 1px line
        const showSeparator = idx > 0 && !active && !prevActive;
        return (
          <div
            key={tab.id}
            role="tab"
            aria-selected={active}
            className={
              // 텍스트 크기: text-[length:...] type modifier 명시 — color 와 conflict 방지
              "group relative flex h-9 shrink-0 items-center gap-[var(--bs-space-2)] rounded-t-[10px] px-[var(--bs-space-3)] text-[length:var(--bs-fs-2xs)] " +
              (active
                ? "bg-[var(--bs-gray-50)] text-[color:var(--bs-ink)] font-semibold"
                : "text-[color:var(--bs-gray-500)] hover:bg-[var(--bs-gray-100)] hover:text-[color:var(--bs-ink)]")
            }
          >
            {showSeparator && (
              <span aria-hidden className="pointer-events-none absolute left-0 top-1/2 h-4 w-px -translate-y-1/2 bg-[var(--bs-gray-300)]" />
            )}
            <button
              type="button"
              onClick={() => activateTab(tab.id)}
              className="flex items-center gap-[var(--bs-space-2)] outline-none"
            >
              <TabIcon type={tab.type} />
              <span className="max-w-[180px] truncate">{tab.title}</span>
            </button>
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); closeTab(tab.id); }}
              aria-label={`${tab.title} 탭 닫기`}
              className={
                "flex h-5 w-5 items-center justify-center rounded-[var(--bs-radius-sm)] transition-colors " +
                (active
                  ? "text-[var(--bs-gray-500)] hover:bg-[var(--bs-gray-200)] hover:text-[var(--bs-ink)]"
                  : "text-[var(--bs-gray-400)] opacity-70 hover:opacity-100 hover:bg-[var(--bs-gray-300)] hover:text-[var(--bs-ink)]")
              }
            >
              <X className="h-3 w-3" />
            </button>
          </div>
        );
      })}
      <button
        type="button"
        onClick={() => openTab("home", { forceNew: true })}
        aria-label="새 탭"
        className="ml-[var(--bs-space-1)] mb-[var(--bs-space-1)] flex h-7 w-7 shrink-0 items-center justify-center rounded-[var(--bs-radius-sm)] text-[var(--bs-gray-500)] transition-colors hover:bg-[var(--bs-gray-200)] hover:text-[var(--bs-ink)]"
      >
        <Plus className="h-4 w-4" />
      </button>
    </div>
  );
}
